import React, { useState, useCallback } from 'react';
import { Key, CheckCircle, XCircle, Zap, Leaf, Loader2 } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { HuggingFaceProvider } from '../services/providers/HuggingFaceProvider';
import { ReplicateProvider } from '../services/providers/ReplicateProvider';

export default function SettingsPage() {
  const { tier, setTier, apiKeys, setApiKey, jobs } = useApi();

  const totalCost = jobs.filter(j => j.status === 'completed').reduce((s, j) => s + (j.estimatedCost ?? 0), 0);
  const jobCount = jobs.length;
  const byProvider: Record<string, { count: number; cost: number }> = {};
  const byOperation: Record<string, { count: number; cost: number }> = {};
  for (const j of jobs) {
    const cost = j.status === 'completed' ? (j.estimatedCost ?? 0) : 0;
    if (!byProvider[j.provider]) byProvider[j.provider] = { count: 0, cost: 0 };
    byProvider[j.provider].count += 1;
    byProvider[j.provider].cost += cost;
    if (!byOperation[j.operation]) byOperation[j.operation] = { count: 0, cost: 0 };
    byOperation[j.operation].count += 1;
    byOperation[j.operation].cost += cost;
  }

  return (
    <div className="max-w-2xl mx-auto px-6 py-8 sage-animate-fade-in">
      <h1 className="text-xl font-semibold mb-6" style={{ fontFamily: 'var(--sage-font-serif)' }}>Settings</h1>

      {/* Tier toggle */}
      <Section title="Provider Tier">
        <p className="text-xs mb-3" style={{ color: 'var(--sage-text-muted)' }}>
          Free uses HuggingFace (rate-limited). Premium uses Replicate/fal.ai (faster, better quality, ~$0.01-0.05/op).
        </p>
        <div className="flex gap-2">
          <TierButton
            active={tier === 'free'}
            onClick={() => setTier('free')}
            icon={<Leaf size={16} />}
            label="Free"
            desc="HuggingFace / Cloudflare AI"
          />
          <TierButton
            active={tier === 'paid'}
            onClick={() => setTier('paid')}
            icon={<Zap size={16} />}
            label="Premium"
            desc="Replicate / fal.ai"
          />
        </div>
      </Section>

      {/* API Keys */}
      <Section title="API Keys">
        <ApiKeyInput
          label="HuggingFace"
          value={apiKeys.huggingface || ''}
          onChange={v => setApiKey('huggingface', v)}
          placeholder="hf_..."
          helpText="Free at huggingface.co/settings/tokens"
          providerType="huggingface"
        />
        <ApiKeyInput
          label="Replicate"
          value={apiKeys.replicate || ''}
          onChange={v => setApiKey('replicate', v)}
          placeholder="r8_..."
          helpText="Get at replicate.com/account/api-tokens"
          providerType="replicate"
        />
        <ApiKeyInput
          label="fal.ai"
          value={apiKeys.fal || ''}
          onChange={v => setApiKey('fal', v)}
          placeholder="fal_..."
          helpText="Get at fal.ai/dashboard/keys"
          providerType="fal"
        />
      </Section>

      {/* Usage */}
      <Section title="Usage">
        {jobCount === 0 ? (
          <p className="text-xs" style={{ color: 'var(--sage-text-muted)' }}>No operations yet this session.</p>
        ) : (
          <>
            <div className="mb-4">
              <p className="text-3xl font-semibold" style={{ color: 'var(--sage-primary)' }}>
                ~${totalCost.toFixed(2)}
              </p>
              <p className="text-xs" style={{ color: 'var(--sage-text-muted)' }}>
                {jobCount} operation{jobCount === 1 ? '' : 's'} this session
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <UsageTable title="By provider" rows={byProvider} />
              <UsageTable title="By operation" rows={byOperation} />
            </div>
            <p className="text-[10px] mt-3" style={{ color: 'var(--sage-text-muted)' }}>
              Usage is session-only (not persisted). Clears when you refresh.
            </p>
          </>
        )}
      </Section>

      {/* Storage info */}
      <Section title="Storage">
        <StorageInfo />
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h2 className="text-sm font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--sage-text-muted)' }}>
        {title}
      </h2>
      <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--sage-surface)' }}>
        {children}
      </div>
    </div>
  );
}

function TierButton({ active, onClick, icon, label, desc }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string; desc: string;
}) {
  return (
    <button
      onClick={onClick}
      className="flex-1 flex items-center gap-3 p-3 rounded-lg transition-colors"
      style={{
        backgroundColor: active ? 'color-mix(in srgb, var(--sage-primary) 15%, transparent)' : 'var(--sage-bg)',
        border: `1px solid ${active ? 'var(--sage-primary)' : 'var(--sage-white-10)'}`,
        color: active ? 'var(--sage-primary)' : 'var(--sage-text-muted)',
      }}
    >
      {icon}
      <div className="text-left">
        <p className="text-sm font-medium" style={{ color: active ? 'var(--sage-text)' : 'var(--sage-text-muted)' }}>{label}</p>
        <p className="text-[10px]" style={{ color: 'var(--sage-text-muted)' }}>{desc}</p>
      </div>
    </button>
  );
}

function ApiKeyInput({ label, value, onChange, placeholder, helpText, providerType }: {
  label: string; value: string; onChange: (v: string) => void; placeholder: string; helpText: string; providerType: 'huggingface' | 'replicate' | 'fal';
}) {
  const [visible, setVisible] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'ok' | 'fail'>('idle');
  const hasKey = value.length > 0;

  const testConnection = useCallback(async () => {
    if (!value) return;
    setTestStatus('testing');
    try {
      let ok = false;
      if (providerType === 'huggingface') {
        const provider = new HuggingFaceProvider(value);
        ok = await provider.healthCheck();
      } else if (providerType === 'replicate') {
        const provider = new ReplicateProvider(value);
        ok = await provider.healthCheck();
      }
      setTestStatus(ok ? 'ok' : 'fail');
    } catch {
      setTestStatus('fail');
    }
    setTimeout(() => setTestStatus('idle'), 5000);
  }, [value, providerType]);

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium" style={{ color: 'var(--sage-text)' }}>{label}</label>
        <div className="flex items-center gap-2">
          {testStatus === 'ok' && (
            <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--sage-primary)' }}>
              <CheckCircle size={10} /> connected
            </span>
          )}
          {testStatus === 'fail' && (
            <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--sage-danger)' }}>
              <XCircle size={10} /> failed
            </span>
          )}
          {hasKey && testStatus === 'idle' && (
            <span className="flex items-center gap-1 text-[10px]" style={{ color: 'var(--sage-primary)' }}>
              <CheckCircle size={10} /> configured
            </span>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 rounded-lg text-sm bg-transparent outline-none"
          style={{
            border: '1px solid var(--sage-white-10)',
            color: 'var(--sage-text)',
            backgroundColor: 'var(--sage-bg)',
          }}
        />
        <button
          onClick={() => setVisible(!visible)}
          className="px-3 py-2 rounded-lg text-xs"
          style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)', border: '1px solid var(--sage-white-10)' }}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
        {hasKey && (
          <button
            onClick={testConnection}
            disabled={testStatus === 'testing'}
            className="px-3 py-2 rounded-lg text-xs flex items-center gap-1 disabled:opacity-50"
            style={{ backgroundColor: 'var(--sage-bg)', color: 'var(--sage-text-muted)', border: '1px solid var(--sage-white-10)' }}
          >
            {testStatus === 'testing' ? <Loader2 size={12} className="sage-animate-spin" /> : null}
            Test
          </button>
        )}
      </div>
      <p className="text-[10px] mt-1" style={{ color: 'var(--sage-text-muted)' }}>{helpText}</p>
    </div>
  );
}

function StorageInfo() {
  const [info, setInfo] = useState<string>('Calculating...');
  React.useEffect(() => {
    navigator.storage?.estimate().then(est => {
      const used = est.usage ? (est.usage / 1024 / 1024).toFixed(1) : '0';
      const quota = est.quota ? (est.quota / 1024 / 1024 / 1024).toFixed(1) : '?';
      setInfo(`${used} MB used of ${quota} GB available`);
    }).catch(() => setInfo('Unable to estimate storage'));
  }, []);

  return <p className="text-xs" style={{ color: 'var(--sage-text-muted)' }}>{info}</p>;
}

function UsageTable({ title, rows }: { title: string; rows: Record<string, { count: number; cost: number }> }) {
  const entries = Object.entries(rows).sort((a, b) => b[1].cost - a[1].cost);
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: 'var(--sage-text-muted)' }}>{title}</p>
      <div className="flex flex-col gap-1">
        {entries.map(([label, { count, cost }]) => (
          <div key={label} className="grid grid-cols-2 text-xs" style={{ color: 'var(--sage-text)' }}>
            <span className="truncate">{label}</span>
            <span className="text-right" style={{ color: 'var(--sage-text-muted)' }}>
              {count} op{count === 1 ? '' : 's'} — ~${cost.toFixed(2)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
